import { io, type Socket } from "socket.io-client";
import { getApiBaseURL } from "./api";
import { notifySessionRevoked } from "../utils/sessionEvents";

/**
 * Single shared socket for the whole app.
 *
 * Deliberately a plain module rather than React context: the axios layer is one
 * too, and both need to be reachable from non-component code (interceptors,
 * the session-revoked broker).
 */

let socket: Socket | null = null;

/** Raised on every successful (re)connection after the first. */
export const SOCKET_RESYNC_EVENT = "livecargo:socket-resync";

/**
 * Raised when a NEW socket instance exists.
 *
 * Two cases make this necessary rather than cosmetic:
 *  - React runs child effects before parent effects, so a page component can
 *    try to register listeners before SocketBridge (its parent) has connected.
 *  - After logout → login the singleton is a different object, and listeners
 *    still bound to the old one are silently dead.
 * `useSocketEvent` listens for this and re-binds.
 */
export const SOCKET_READY_EVENT = "livecargo:socket-ready";

/**
 * Reuses `getApiBaseURL()` and strips the `/api` mount point — Socket.IO
 * connects to the origin, not to the REST path.
 *
 * Deliberately derived rather than rewritten: a second hand-maintained URL
 * scheme is how one of the two ends up on the wrong host or port. (The dev
 * branch of getApiBaseURL hardcodes :5000 while the backend .env sets
 * PORT=5001 — whichever is correct, both channels now agree.)
 */
const getSocketURL = (): string =>
  getApiBaseURL().replace(/\/api\/?$/, "");

const getSubdomain = (): string => window.location.hostname.split(".")[0];

export const getSocket = (): Socket | null => socket;

export const connectSocket = (): Socket | null => {
  const token = localStorage.getItem("token");
  if (!token) return null;

  if (socket) {
    // Reuse the existing connection; a fresh login re-enters here with a new
    // token, so refresh the handshake payload before reconnecting.
    socket.auth = { token, subdomain: getSubdomain() };
    if (!socket.connected) socket.connect();
    return socket;
  }

  socket = io(getSocketURL(), {
    path: "/socket.io",
    // The handshake carries the tenant explicitly: in production the API is its
    // own host (api-v2.livecargo.ee), so the server cannot read the subdomain
    // off the hostname — the same reason REST sends `x-subdomain`.
    auth: { token, subdomain: getSubdomain() },
    withCredentials: true,
    autoConnect: false,
    transports: ["websocket", "polling"],
  });

  socket.on("connect_error", (err: Error & { data?: { code?: string } }) => {
    // A revoked session must not retry — the refresh token carries the same
    // dead session id, so every attempt would fail identically. Hand off to the
    // existing broker, which shows the already-mounted SessionExpiredModal.
    if (err?.data?.code === "SESSION_REVOKED") {
      socket?.disconnect();
      notifySessionRevoked(err.message);
    }
  });

  // Server-initiated revocation (a login on another device).
  //
  // `endSession` sends this frame for every ending, including the user's own
  // sign-out — so the tab that clicked Logout was being told it had been signed
  // in elsewhere. The server now says which it is; a deliberate logout severs
  // the socket and nothing more, because the logout flow already owns what
  // happens next.
  socket.on("session:revoked", (payload?: { reason?: string }) => {
    socket?.disconnect();
    if (payload?.reason === "LOGOUT") return;
    notifySessionRevoked();
  });

  // Events raised while the socket was down are simply gone — there is no
  // replay buffer. Tell the app to refetch instead of pretending it is current.
  socket.io.on("reconnect", () => {
    window.dispatchEvent(new CustomEvent(SOCKET_RESYNC_EVENT));
  });

  socket.connect();

  // Announce after connect() so anything re-binding finds a live instance.
  window.dispatchEvent(new CustomEvent(SOCKET_READY_EVENT));

  return socket;
};

export const disconnectSocket = () => {
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
};

/**
 * The entity rooms a client may ask to join.
 *
 * Mirrors `SUBSCRIBABLE` in backend/socket/index.js, which is the authority:
 * each of these is a room the server will only put this socket into after
 * re-checking the entity against the same rule the REST read path uses.
 */
export type SocketRoomType = "shipment" | "work" | "containerLine";

/**
 * Join/leave an entity room. The server re-checks that the entity belongs to
 * the authenticated tenant before honouring it, so passing an id here is a
 * request, not a grant.
 */
export const subscribeTo = (type: SocketRoomType, id: string) => {
  socket?.emit("subscribe", { type, id });
};

export const unsubscribeFrom = (type: SocketRoomType, id: string) => {
  socket?.emit("unsubscribe", { type, id });
};
